/**
 * Format a raw date string as, for example, "February 4, 2026". An empty input
 * gives an em dash, and an unparseable one the raw string.
 *
 * `locale` defaults to `en-US` because most callers are staff tables that have
 * always read English. Pass the active language where the date sits inside a
 * translated sentence; `i18n.resolvedLanguage` can be passed straight through,
 * since `Intl` accepts all four.
 */
export function formatLongDate(
  raw: string | null | undefined,
  locale = 'en-US',
): string {
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Largest unit first, so a 90-minute gap reads "1 hour ago", not "90 minutes ago". */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * Format an instant as "3 hours ago" in the caller's language. Done
 * client-side, where `Intl.RelativeTimeFormat` translates it.
 */
export function formatRelativeTime(
  raw: string | null | undefined,
  locale: string,
): string {
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  for (const [unit, unitSeconds] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= unitSeconds) {
      return formatter.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return formatter.format(seconds, 'second');
}
